/**
 * License data contract (EDUS-2725) — the single source shared by the
 * backend LicensingService, the login router, and the platform-admin console.
 * Backend-only concerns (queried SKUs, ignored consumer domains, the raw
 * is-entitlement response shapes, LicenseCheckError, the QA facts builder)
 * stay in the backend's licensing.models.ts.
 *
 * Tier names are the RAW vocabulary emitted by the is-entitlement Chargify
 * function — deliberately NOT the legacy relay's renamed set (the relay
 * cosmetically mapped 'Consulting' → 'Consultant').
 */
export const LICENSE_TIERS = [
  'Paid',
  'Trial',
  'Lapsed',
  'Free',
  'Consulting',
  'Unlicensed',
] as const;

export type LicenseTier = (typeof LICENSE_TIERS)[number];

/**
 * Chargify bundle handles → the names a customer would recognise. Used by the
 * lapsed screen's "…as part of your <BUNDLE> subscription" sentence
 * (EDUS-2810); 'Admin Unlimited Bundle' is the Figma frame's own wording for
 * ait-atu.
 *
 * Deliberately NOT exhaustive — see {@link UNKNOWN_BUNDLE_DISPLAY_NAME}.
 */
export const BUNDLE_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  'ait-atu': 'Admin Unlimited Bundle',
  'ait-gop': 'Gopher Pack',
};

/**
 * What we call a subscription we can't name precisely: either the customer
 * bought Gopher directly (no bundle at all) or the handle isn't one we map.
 *
 * 'CDW' is deliberately vague but never WRONG — every one of these customers
 * does hold a CDW subscription. That matters because it lets the frame's
 * sentence render verbatim in every case; the alternative was dropping the
 * "as part of your <BUNDLE>" clause and shipping a second sentence product
 * never wrote. Showing the raw handle ('ait-xyz') was never an option
 * (product, 2026-08-20).
 */
export const UNKNOWN_BUNDLE_DISPLAY_NAME = 'CDW';

/** A customer-facing bundle name; always renders, never null. */
export function bundleDisplayName(handle: string | null): string {
  return (
    (handle ? BUNDLE_DISPLAY_NAMES[handle] : undefined) ??
    UNKNOWN_BUNDLE_DISPLAY_NAME
  );
}

/** Per-SKU facts, straight from the is-entitlement response. */
export interface SubscriptionFacts {
  tier: LicenseTier;
  /** ISO timestamp from current_period_ends_at — display/diagnostics only; access trusts tier alone. */
  expiry: string | null;
  /** Which bundle granted this entitlement (e.g. 'ait-atu'), if any. */
  bundle: string | null;
  paidEndedAt: string | null;
  trialEndedAt: string | null;
}

/** The consolidated, dominance-resolved "can you use Gopher" answer. */
export interface LicenseAccess {
  tier: LicenseTier;
  expiry: string | null;
  bundle: string | null;
  /** Which subscription won dominance; null when neither access SKU was returned. */
  activeSku: string | null;
}

/** The pure license facts — this is the shape persisted in Redis. */
export interface LicenseFacts {
  /** false = the 400 "couldn't find customer" business answer (never cached). */
  inChargify: boolean;
  /** Consumers deciding access (the login router) read ONLY this. */
  access: LicenseAccess;
  /** Every SKU returned for the queried list — for future per-feature checks. */
  subscriptions: Partial<Record<string, SubscriptionFacts>>;
}

/**
 * What checkLicense() returns: the facts plus provenance. fromCache is stamped
 * at return time (true on a Redis hit, false on a live read) and is
 * deliberately NOT part of the persisted LicenseFacts shape.
 */
export interface LicenseCheckResult extends LicenseFacts {
  fromCache: boolean;
}
